import { useEffect, useState } from "react";
import api from "../../lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Cell } from "recharts";
import { Loader2, Users, Building2, CheckCircle2, ShieldCheck, TrendingUp, Target, Activity, Trophy } from "lucide-react";

const scoreColor = (v) => (v >= 85 ? "#10B981" : v >= 70 ? "#0055FF" : v >= 50 ? "#F59E0B" : "#EF4444");
const FUNNEL_COLORS = ["#0055FF", "#6366F1", "#8B5CF6", "#F59E0B", "#10B981"];
const READINESS_COLOR = { "Interview Ready": "#10B981", "Almost Ready": "#F59E0B", "Needs Improvement": "#EF4444", "Not Started": "#94A3B8", "Not Assessed": "#94A3B8" };

const Card = ({ children, className = "", ...rest }) => <div className={`bg-white rounded-2xl border border-slate-200 shadow-card ${className}`} {...rest}>{children}</div>;
function Kpi({ icon: Icon, label, value, suffix, accent }) {
  return (
    <Card className="p-5" >
      <span className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: accent + "1A" }}><Icon className="h-5 w-5" style={{ color: accent }} /></span>
      <div className="num font-bold text-3xl text-ink mt-3">{value}<span className="text-base text-ink-faint">{suffix}</span></div>
      <div className="text-xs font-semibold text-ink-faint mt-0.5">{label}</div>
    </Card>
  );
}
const ACTION_LABEL = {
  candidate_added: "added a candidate", client_added: "added a client", recommended: "recommended a candidate",
  status_changed: "updated a pipeline status", published: "published a credential", interview_assigned: "assigned an interview",
  interview_scored: "scored an interview", permissions_updated: "updated permissions", member_added: "added a team member",
  credits_purchased: "purchased credits",
};

export default function AgencyAnalytics() {
  const [d, setD] = useState(null);
  const [activity, setActivity] = useState([]);
  useEffect(() => {
    api.get("/agency/analytics").then((r) => setD(r.data)).catch(() => {});
    api.get("/agency/activity?limit=25").then((r) => setActivity(r.data.activity)).catch(() => {});
  }, []);
  if (!d) return <div className="min-h-[40vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;
  const k = d.kpis;

  return (
    <div className="space-y-6" data-testid="agency-analytics">
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-brand mb-1">Talent Intelligence</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Agency Analytics</h1>
        <p className="text-sm text-ink-soft mt-1">Placement funnel, client conversion and recruiter performance across your desk.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Users} label="Candidates" value={k.candidates} accent="#0055FF" />
        <Kpi icon={Building2} label="Active Clients" value={k.clients} accent="#8B5CF6" />
        <Kpi icon={Target} label="Recommendations" value={k.recommendations} accent="#6366F1" />
        <Kpi icon={CheckCircle2} label="Placements (Selected)" value={k.selected} accent="#10B981" />
        <Kpi icon={TrendingUp} label="Conversion Rate" value={k.conversion_rate} suffix="%" accent="#F59E0B" />
        <Kpi icon={ShieldCheck} label="Credentials Published" value={k.published} accent="#0EA5E9" />
        <Kpi icon={CheckCircle2} label="Interviews Completed" value={k.interviews_completed} accent="#0055FF" />
        <Kpi icon={Trophy} label="Avg Einstein Score™" value={k.avg_einstein} accent={scoreColor(k.avg_einstein)} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-6">
          <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><Target className="h-4 w-4 text-brand" /> Placement Funnel</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.funnel} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="stage" tick={{ fontSize: 10 }} interval={0} angle={-12} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>{d.funnel.map((e, i) => <Cell key={i} fill={FUNNEL_COLORS[i % FUNNEL_COLORS.length]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6">
          <div className="font-heading font-bold text-ink mb-4">Einstein Score™ Distribution</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.score_distribution} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="band" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" radius={[6, 6, 0, 0]} fill="#0055FF" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-6 lg:col-span-2">
          <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><Trophy className="h-4 w-4 text-brand" /> Recruiter Performance</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-faint border-b border-slate-200"><th className="px-3 py-2.5">Recruiter</th><th className="px-3 py-2.5">Candidates</th><th className="px-3 py-2.5">Recommendations</th><th className="px-3 py-2.5">Selected</th><th className="px-3 py-2.5">Avg Einstein™</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {d.recruiter_performance.map((r) => (
                  <tr key={r.recruiter} data-testid={`recruiter-perf-${r.recruiter}`}>
                    <td className="px-3 py-3 font-semibold text-ink">{r.recruiter}</td>
                    <td className="px-3 py-3 num text-ink-soft">{r.candidates}</td>
                    <td className="px-3 py-3 num text-ink-soft">{r.recommendations}</td>
                    <td className="px-3 py-3 num font-bold text-emerald-600">{r.selected}</td>
                    <td className="px-3 py-3 num font-bold" style={{ color: scoreColor(r.avg_einstein) }}>{r.avg_einstein}</td>
                  </tr>
                ))}
                {d.recruiter_performance.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-ink-soft">No recruiter data yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
        <Card className="p-6">
          <div className="font-heading font-bold text-ink mb-4">Candidate Readiness</div>
          <div className="space-y-3">
            {d.readiness_distribution.map((r) => (
              <div key={r.level} className="flex items-center gap-3">
                <span className="h-3 w-3 rounded-full shrink-0" style={{ background: READINESS_COLOR[r.level] || "#94A3B8" }} />
                <span className="text-sm text-ink flex-1">{r.level}</span>
                <span className="num font-bold text-ink">{r.count}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-6 lg:col-span-2">
          <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><Building2 className="h-4 w-4 text-brand" /> Client Conversion</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-faint border-b border-slate-200"><th className="px-3 py-2.5">Client</th><th className="px-3 py-2.5">Recommended</th><th className="px-3 py-2.5">Selected</th><th className="px-3 py-2.5">Conversion</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {d.client_conversion.map((c) => (
                  <tr key={c.client}>
                    <td className="px-3 py-3 font-semibold text-ink">{c.client}</td>
                    <td className="px-3 py-3 num text-ink-soft">{c.recommended}</td>
                    <td className="px-3 py-3 num text-emerald-600 font-bold">{c.selected}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2"><div className="w-24 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-brand" style={{ width: `${c.conversion}%` }} /></div><span className="num text-xs font-bold text-ink">{c.conversion}%</span></div>
                    </td>
                  </tr>
                ))}
                {d.client_conversion.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-ink-soft">No client data yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
        <Card className="p-6" data-testid="agency-activity-feed">
          <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><Activity className="h-4 w-4 text-brand" /> Recent Activity</div>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {activity.map((a) => (
              <div key={a.id} className="flex gap-3 text-sm">
                <span className="h-2 w-2 rounded-full bg-brand mt-1.5 shrink-0" />
                <div><div className="text-ink"><b>{a.actor_name}</b> <span className="text-ink-soft">{ACTION_LABEL[a.action] || a.action}</span></div>
                  <div className="text-[11px] text-ink-faint">{a.summary}</div>
                  <div className="text-[10px] text-ink-faint num">{new Date(a.created_at).toLocaleString()}</div></div>
              </div>
            ))}
            {activity.length === 0 && <div className="text-sm text-ink-soft text-center py-6">No activity yet.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
